import React, { useCallback, useEffect, useState } from 'react'
import styles from '../registration.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import UserMessageScreen from 'components/generic/UserMessageScreen'
import { MESSAGE_SCREENS, MessageTypes } from 'constants/message-screens'
import { useCountries } from 'hooks/use-countries'
import { Formik, Form } from 'formik'
import InputWTN from 'components/generic/InputWTN'
import DateSelectGroup from 'components/generic/DateSelectGroup'
import withTooltip from 'components/generic/hoc/withTooltip'
import { InfoIcon } from 'components/Icons'
import { activateUser, getAccountDetails, updateAndActivateUser } from 'actions/user-actions'
import {
  convertResidenceLongFormToAlpha3,
  formatUiDate,
  getQueryParam,
  getSignInUrl,
  getSignOutUrl,
  isFormattedDateValid,
  isOfAge,
} from 'utils'
import InfoBox from 'components/generic/InfoBox'
import BtnPrimary from 'components/generic/BtnPrimary'
import SearchFieldWTN from 'components/generic/SearchFieldWTN'
import LoadingSpinner from '../common/LoadingSpinner'
import { SexEnum, SignUpTypeEnum } from 'types/api/enums'
import { AccountDetailsFetchType, UserStatus } from 'types/global'
import ScrollToFormError from 'components/generic/ScrollToFormError'
import FormInputGroup from 'components/generic/FormInputGroup'
import { makeValidationFunc } from 'forms/validation/validators'
import { useActivateUserValidationSchema } from 'forms/validation/schemas'
import SexInputGroup from 'components/generic/SexInputGroup'
import { usePlaceholders, useFormLabels } from 'forms/common/translations'
import { FormikNames } from 'types/forms/formik-names'
import { useMemo } from 'react'
import FormCheckbox from 'components/generic/FormCheckbox'
import cx from 'classnames'
import TermsAndPolicy, { TermsAndPolicyType } from '../common/TermsAndPolicy'
import { Trans, useTranslation } from 'react-i18next'
import { ACTIVATION_PAGE_BASE_URL, CONTACT_US_HREF, RegistrationData } from 'constants/global'
import UserApi from 'api/rest/user'
import withOnlyAllowLoggedIn from 'components/generic/hoc/withOnlyAllowLoggedIn'
import ErrorScreen, { ErrorScreenProps } from 'components/generic/ErrorScreen'
import Storage from 'modules/storage'
import { createRegLog, withRegLogAsync } from 'modules/reg-logger'
import ParentalConsent from '../common/ParentalConsent'
import { Persona } from 'forms/common/options'
import PersonaSelection from '../common/PersonaSelection'
import { useSubmitPublicRmg } from '../hooks'
import { PersonPSQAnswerInput } from 'types/api/types'

const canActivate = withRegLogAsync(UserApi.canActivate.bind(UserApi), 'canActivate')

const handleActivationNotAllowed = () => {
  createRegLog('inviteRequired')
  UserApi.signOut(`${ACTIVATION_PAGE_BASE_URL}&invite-required=true`)
}

const RedirectActiveUser = () => {
  useEffect(() => {
    Storage.clearUser()
    UserApi.redirectToOwnProfilePage()
  }, [])
  return <LoadingSpinner />
}

const ActivateWalkUpUser = ({
  data,
  publicRmg,
}: {
  data: RegistrationData
  publicRmg: PersonPSQAnswerInput[] | null
}) => {
  const dispatch = useDispatch()
  const canActivate = useSubmitPublicRmg(publicRmg)

  useEffect(() => {
    if (canActivate) {
      dispatch(
        activateUser({
          consents: {
            commConsentItf: data.commConsentItf,
            commConsentNa: data.commConsentNa,
          },
          regType: SignUpTypeEnum.WALKUP,
        })
      )
    }
  }, [canActivate, data, dispatch])

  return <LoadingSpinner />
}

const InfoResidence = withTooltip(InfoIcon)

const ResidenceLabelWithTooltip = () => {
  const { t } = useTranslation()
  const labels = useFormLabels()
  return (
    <>
      <label
        htmlFor={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
        data-formik-error={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
        className={cx(styles.formLabel, styles.inlineLabel)}
      >
        {labels.countryOfResidence}
      </label>{' '}
      <InfoResidence id='residence-tooltip'>
        <p>{t('residenceTooltip.partOne', { ns: 'strFastTrackRegistration' })}</p>
        <p className={styles.mb0}>
          {t('residenceTooltip.partTwo', { ns: 'strFastTrackRegistration' })}
        </p>
      </InfoResidence>
    </>
  )
}

const ActivateUser = () => {
  const { t } = useTranslation('strCommonUI')
  const labels = useFormLabels()
  const placeholders = usePlaceholders()
  const contactUsUrl = getSignOutUrl(window.location.origin + '/eng/contact')
  const [networkError, setNetworkError] = useState<ErrorScreenProps | null>(null)
  const dispatch = useDispatch()
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const accountDetails = useSelector((state: AppState) => state.user.accountDetails)
  const accountDetailsUpdating = useSelector((state: AppState) => state.user.accountDetailsUpdating)
  const accountDetailsError = useSelector((state: AppState) => state.user.accountDetailsError)
  const activationInProgress = useSelector((state: AppState) => state.user.activationInProgress)
  const activationError = useSelector((state: AppState) => state.user.activationError)
  const userStatus = useSelector((state: AppState) => state.user.userStatus)
  const displayError: ErrorScreenProps | null = useMemo(() => {
    const accountDetailsErr = accountDetailsError && {
      type: MessageTypes.FAST_TRACK_ERROR,
      message: accountDetailsError.message,
    }
    const activationErr = activationError && {
      type: MessageTypes.ACTIVATION_ERROR,
      message: activationError.message,
    }
    return activationErr || accountDetailsErr || networkError
  }, [activationError, accountDetailsError, networkError])
  const {
    isValidCountryCode,
    getNationality,
    getCountryName,
    residenceOptions,
    nationalityOptions,
  } = useCountries()
  const regData = useMemo(() => Storage.getRegistrationData(), [])
  const publicRmg = useMemo(() => Storage.getPublicRmg(), [])

  const parentalConsentRequired =
    !regData && (!accountDetails?.birthDate || !isOfAge(accountDetails.birthDate))
  const activateUserValidationSchema = useActivateUserValidationSchema()
  const handleValidate = useCallback(
    values => {
      const allFields = [
        FormikNames.NATIVE_GIVEN_NAME,
        FormikNames.NATIVE_FAMILY_NAME,
        FormikNames.BIRTH_DATE,
        FormikNames.SEX,
        FormikNames.NATIONALITY_CODE,
        FormikNames.COUNTRY_OF_RESIDENCE_CODE,
        FormikNames.PARENT_GUARDIAN_NAME,
        FormikNames.PARENT_GUARDIAN_EMAIL,
        FormikNames.PERSONA_DETAIL,
      ]

      const parentalConsentFields = [
        FormikNames.PARENT_GUARDIAN_NAME,
        FormikNames.PARENT_GUARDIAN_EMAIL,
      ]
      const omitValidationFields: string[] = []

      if (!parentalConsentRequired) parentalConsentFields.forEach(f => omitValidationFields.push(f))

      if (values[FormikNames.PERSONA] !== Persona.OTHER)
        omitValidationFields.push(FormikNames.PERSONA_DETAIL)

      const fieldsToValidate = [...allFields].filter(f => !omitValidationFields.includes(f))

      const validate = makeValidationFunc(fieldsToValidate, activateUserValidationSchema)
      return validate(values)
    },
    [parentalConsentRequired, activateUserValidationSchema]
  )

  useEffect(() => {
    if (isLoggedIn && userStatus === UserStatus.PENDING) {
      const handlePendingUser = async () => {
        try {
          const canUserActivate = await canActivate()
          if (canUserActivate) {
            dispatch(getAccountDetails(AccountDetailsFetchType.REGISTRATION))
          } else {
            handleActivationNotAllowed()
          }
        } catch (err) {
          setNetworkError({
            type: MessageTypes.FAST_TRACK_ERROR,
            message: `Failed to check if user can activate. ${(err as Error)?.message}`,
          })
        }
      }

      handlePendingUser()
    }
  }, [dispatch, userStatus, isLoggedIn])

  if (userStatus === UserStatus.ACTIVE) return <RedirectActiveUser />

  if (displayError) return <ErrorScreen {...displayError} backLink={contactUsUrl} />

  if (!userStatus || !accountDetails || activationInProgress || accountDetailsUpdating)
    return <LoadingSpinner />

  if (regData?.isWalkUpUser) return <ActivateWalkUpUser data={regData} publicRmg={publicRmg} />

  return (
    <>
      <h1>{t('accountCreated', { ns: 'strFastTrackRegistration' })}</h1>

      <p className={styles.introText}>{t('checkDetails', { ns: 'strFastTrackRegistration' })}</p>

      <Formik
        initialValues={{
          [FormikNames.NATIVE_GIVEN_NAME]: accountDetails?.nativeGivenName || '',
          [FormikNames.NATIVE_FAMILY_NAME]: accountDetails?.nativeFamilyName || '',
          [FormikNames.BIRTH_DATE]: accountDetails?.birthDate || '',
          [FormikNames.DOB_DAY]: accountDetails?.birthDate ? null : '',
          [FormikNames.DOB_MONTH]: accountDetails?.birthDate ? null : '',
          [FormikNames.DOB_YEAR]: accountDetails?.birthDate ? null : '',
          [FormikNames.SEX]: accountDetails?.sex || '',
          [FormikNames.NATIONALITY_CODE]: isValidCountryCode(accountDetails?.nationalityCode)
            ? accountDetails.nationalityCode
            : '',
          [FormikNames.COUNTRY_OF_RESIDENCE_CODE]: isValidCountryCode(
            accountDetails?.countryOfResidenceCode
          )
            ? accountDetails.countryOfResidenceCode
            : '',
          [FormikNames.COMM_CONSENT_ITF]: regData?.commConsentItf || false,
          [FormikNames.COMM_CONSENT_NA]: regData?.commConsentNa || false,
          [FormikNames.PARENT_GUARDIAN_NAME]: regData?.parentFullName || '',
          [FormikNames.PARENT_GUARDIAN_EMAIL]: regData?.parentEmailAddress || '',
          [FormikNames.PERSONA]: '',
          [FormikNames.PERSONA_DETAIL]: '',
        }}
        validate={handleValidate}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(
          {
            nativeGivenName,
            nativeFamilyName,
            birthDate,
            sex,
            nationalityCode,
            countryOfResidenceCode,
            commConsentItf,
            commConsentNa,
            parentGuardianFullName,
            parentGuardianEmail,
            persona,
            personaDetail,
          },
          actions
        ) => {
          let updatedPersonDetails: object = {
            nativeGivenName,
            nativeFamilyName,
            nationalityCode,
            countryOfResidenceCode: convertResidenceLongFormToAlpha3(countryOfResidenceCode),
            birthDate,
            sex,
          }

          if (accountDetails) {
            for (let key in accountDetails) {
              if (accountDetails[key] === updatedPersonDetails[key])
                delete updatedPersonDetails[key]
            }
          }

          if (parentGuardianFullName || parentGuardianEmail) {
            updatedPersonDetails['parentFullName'] = parentGuardianFullName
            updatedPersonDetails['parentEmailAddress'] = parentGuardianEmail
          }

          if (persona) updatedPersonDetails['personaType'] = persona
          if (personaDetail && persona === Persona.OTHER)
            updatedPersonDetails['personaDetail'] = personaDetail

          updatedPersonDetails['signUpType'] = SignUpTypeEnum.FASTTRACK

          dispatch(
            updateAndActivateUser({
              updateInput: updatedPersonDetails,
              consents: {
                commConsentItf,
                commConsentNa,
              },
              regType: SignUpTypeEnum.FASTTRACK,
            })
          )
        }}
      >
        {({ errors, values }) => (
          <Form>
            <div className={styles.activateUserForm}>
              <FormInputGroup
                label={labels.givenName}
                formikName={FormikNames.NATIVE_GIVEN_NAME}
                valueStaticText={values[FormikNames.NATIVE_GIVEN_NAME]}
                fieldAsStaticText={!!accountDetails.nativeGivenName}
                classWrapper={styles.inputGroup}
              >
                <InputWTN
                  id={FormikNames.NATIVE_GIVEN_NAME}
                  name={FormikNames.NATIVE_GIVEN_NAME}
                  className={cx(styles.commonTextInput, styles.input)}
                />
              </FormInputGroup>

              <FormInputGroup
                label={labels.familyName}
                formikName={FormikNames.NATIVE_FAMILY_NAME}
                valueStaticText={values[FormikNames.NATIVE_FAMILY_NAME]}
                fieldAsStaticText={!!accountDetails.nativeFamilyName}
                classWrapper={styles.inputGroup}
              >
                <InputWTN
                  id={FormikNames.NATIVE_FAMILY_NAME}
                  name={FormikNames.NATIVE_FAMILY_NAME}
                  className={cx(styles.commonTextInput, styles.input)}
                />
              </FormInputGroup>

              <FormInputGroup
                label={labels.dateOfBirth}
                formikName={FormikNames.BIRTH_DATE}
                fieldAsStaticText={
                  !!accountDetails.birthDate && isFormattedDateValid(accountDetails.birthDate)
                }
                classWrapper={styles.inputGroup}
                valueStaticText={
                  isFormattedDateValid(accountDetails?.birthDate)
                    ? formatUiDate(accountDetails?.birthDate)
                    : accountDetails?.birthDate
                }
              >
                <DateSelectGroup
                  valueKeys={{
                    fullDate: FormikNames.BIRTH_DATE,
                    day: FormikNames.DOB_DAY,
                    month: FormikNames.DOB_MONTH,
                    year: FormikNames.DOB_YEAR,
                  }}
                />
              </FormInputGroup>

              <FormInputGroup
                label={
                  !!accountDetails.sex ? (
                    labels.sex
                  ) : (
                    <p
                      id={FormikNames.SEX}
                      data-formik-error={FormikNames.SEX}
                      className={styles.formLabel}
                    >
                      {labels.sex}
                    </p>
                  )
                }
                formikName={FormikNames.SEX}
                fieldAsStaticText={!!accountDetails.sex}
                classWrapper={styles.inputGroup}
                valueStaticText={accountDetails.sex === SexEnum.M ? labels.male : labels.female}
              >
                <SexInputGroup formikName={FormikNames.SEX} />
              </FormInputGroup>

              <FormInputGroup
                label={labels.nationality}
                formikName={FormikNames.NATIONALITY_CODE}
                fieldAsStaticText={
                  !!accountDetails.nationalityCode &&
                  isValidCountryCode(accountDetails.nationalityCode)
                }
                classWrapper={styles.inputGroup}
                valueStaticText={getNationality(accountDetails?.nationalityCode)}
              >
                <SearchFieldWTN
                  options={nationalityOptions}
                  name={FormikNames.NATIONALITY_CODE}
                  placeholderText={placeholders.searchField.nationality}
                />
              </FormInputGroup>

              <FormInputGroup
                label={
                  !!accountDetails.countryOfResidenceCode &&
                  !accountDetails.isCountryOfResidenceUpdateable ? (
                    labels.countryOfResidence
                  ) : (
                    <ResidenceLabelWithTooltip />
                  )
                }
                formikName={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
                fieldAsStaticText={
                  !!accountDetails.countryOfResidenceCode &&
                  !accountDetails.isCountryOfResidenceUpdateable
                }
                classWrapper={styles.inputGroup}
                valueStaticText={getCountryName(accountDetails?.countryOfResidenceCode)}
              >
                <SearchFieldWTN
                  options={residenceOptions}
                  name={FormikNames.COUNTRY_OF_RESIDENCE_CODE}
                  placeholderText={placeholders.searchField.countryOfResidence}
                />
              </FormInputGroup>

              <PersonaSelection
                persona={values[FormikNames.PERSONA]}
                personaDetail={values[FormikNames.PERSONA_DETAIL]}
                hasErrorPersonaDetail={!!errors[FormikNames.PERSONA_DETAIL]}
              />
            </div>

            <InfoBox className={styles.infoBox}>
              <Trans ns='strRegister' i18nKey='infoBoxTwo'>
                Are your details incorrect? If so, please click{' '}
                <a href={CONTACT_US_HREF} target='_blank' rel='noopener noreferrer'>
                  here
                </a>
              </Trans>
            </InfoBox>

            {parentalConsentRequired && <ParentalConsent withContainer />}

            {!regData && (
              <div className={cx(styles.consents, styles.mb32)}>
                <FormCheckbox
                  name={FormikNames.COMM_CONSENT_ITF}
                  label={t('commConsentItf', { ns: 'strRegister' })}
                />
                <FormCheckbox
                  name={FormikNames.COMM_CONSENT_NA}
                  label={t('commConsentNa', { ns: 'strRegister' })}
                />
              </div>
            )}
            <TermsAndPolicy type={TermsAndPolicyType.ACTIVATION} className={styles.termsText} />
            <BtnPrimary size={'medium'} type='submit' className={styles.submitBtn}>
              {t('button.accessAccount', { ns: 'strCommonUI' })}
            </BtnPrimary>
            <ScrollToFormError />
          </Form>
        )}
      </Formik>
    </>
  )
}

export default withOnlyAllowLoggedIn(ActivateUser, () => {
  const showInviteRequired = getQueryParam('invite-required') === 'true'
  if (showInviteRequired) return <UserMessageScreen {...MESSAGE_SCREENS.INVITE_REQUIRED} />
  return (
    <UserMessageScreen {...MESSAGE_SCREENS.ACTIVATION_LOGIN_PROMPT} backLink={getSignInUrl()} />
  )
})
